import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import PageHeader from "../components/PageHeader";
import { useAuth } from "../auth/AuthContext";
import { getPickemDivisions, savePickemDivision } from "../api/nflPickem";
import { useNflSeason } from "../hooks/useNflSeason";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
const DEEP_END_RED = "#8B0000";
const SLOTS = [1, 2, 3, 4];

function fmtLockDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * One division's predicted 1-4 order.
 *
 * Each slot is a select listing that division's four teams, and choosing one
 * already placed swaps the two — the same interaction as the PL table
 * predictor, which avoids drag-and-drop on touch screens.
 */
function DivisionCard({ division, value, locked, dirty, onChange, onClear }) {
  const complete = value.filter(Boolean).length === SLOTS.length;
  const teamById = useMemo(() => {
    const m = {};
    (division.teams || []).forEach((t) => { m[String(t.id)] = t; });
    return m;
  }, [division.teams]);

  return (
    <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
         borderRadius="lg" boxShadow="lg" p={{ base: 4, md: 5 }}>
      <Flex align="center" justify="space-between" mb={3} gap={2}>
        <Heading size="sm" color={TOP_BAR_BLUE}>
          {division.conference} {division.division}
        </Heading>
        <HStack spacing={1}>
          {dirty && (
            <Badge colorScheme="orange" fontSize="0.6rem">Unsaved</Badge>
          )}
          {complete ? (
            <Badge colorScheme="green" fontSize="0.6rem">Complete</Badge>
          ) : (
            <Badge colorScheme="gray" fontSize="0.6rem">
              {value.filter(Boolean).length}/4
            </Badge>
          )}
        </HStack>
      </Flex>

      <VStack align="stretch" spacing={2}>
        {SLOTS.map((slot, i) => {
          const current = value[i] ? String(value[i]) : "";
          const team = teamById[current];
          return (
            <HStack key={slot} spacing={2}>
              <Text fontWeight="bold" color={TOP_BAR_BLUE} w="18px" fontSize="sm">
                {slot}
              </Text>
              {team?.logo_url ? (
                <Image src={team.logo_url} boxSize="20px" objectFit="contain" flexShrink={0} />
              ) : (
                <Box boxSize="20px" flexShrink={0} />
              )}
              <Select
                size="sm"
                value={current}
                isDisabled={locked}
                placeholder="— select team —"
                borderColor={current ? TOP_BAR_BLUE : "gray.300"}
                onChange={(e) => onChange(i, e.target.value)}
              >
                {(division.teams || []).map((t) => {
                  const at = value.indexOf(String(t.id));
                  const elsewhere = at >= 0 && at !== i;
                  return (
                    <option key={t.id} value={String(t.id)}>
                      {/* Saying where a team already sits makes the swap
                          predictable rather than a surprise. */}
                      {elsewhere ? `${t.name} (now ${at + 1})` : t.name}
                    </option>
                  );
                })}
              </Select>
              <IconButton
                aria-label={`Clear place ${slot}`}
                title="Remove this team"
                icon={<CloseIcon boxSize="8px" />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                flexShrink={0}
                onClick={() => onClear(i)}
                isDisabled={locked || !current}
              />
            </HStack>
          );
        })}
      </VStack>

    </Box>
  );
}

export default function NflPickemDivisionsPage() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user, logout } = useAuth();
  const { seasonId, loading: seasonLoading } = useNflSeason();
  const effectiveSeasonId = location.state?.seasonId ?? seasonId;

  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const keyFor = (d) => `${d.conference} ${d.division}`;

  /**
   * Fetch predictions and seed the editable copy.
   *
   * `keepDraftsExcept` preserves in-progress edits for every division other
   * than the one named. Saving one division reloads the page data, and
   * reseeding everything from the server wiped unsaved work in the others —
   * fill in AFC East, switch to AFC North, save that, and East was gone.
   * The saved division is deliberately excluded so it picks up whatever the
   * server actually stored.
   */
  const load = useCallback(async (keepDraftsExcept = null) => {
    if (!effectiveSeasonId || !poolId) return;
    setLoading(true);
    setError("");
    try {
      const d = await getPickemDivisions(effectiveSeasonId, poolId);
      setData(d);
      const seeded = {};
      (d.divisions || []).forEach((div) => {
        seeded[`${div.conference} ${div.division}`] =
          (div.prediction || []).map((v) => (v ? String(v) : ""));
      });
      setDrafts((prev) => {
        if (keepDraftsExcept === null) return seeded;
        const merged = { ...seeded };
        Object.entries(prev).forEach(([key, value]) => {
          // A draft matching the server is harmless to keep; one that differs
          // is unsaved work worth protecting.
          if (key !== keepDraftsExcept && (value || []).some(Boolean)) {
            merged[key] = value;
          }
        });
        return merged;
      });
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load predictions.");
    } finally {
      setLoading(false);
    }
  }, [effectiveSeasonId, poolId]);

  useEffect(() => { if (!seasonLoading) load(); }, [load, seasonLoading]);

  /**
   * Place a team, swapping with whichever slot already holds it.
   *
   * This swap existed but could never run: every option was disabled once its
   * team was placed, so a division with all four slots filled could only
   * re-select what each slot already held. A finished division was a frozen
   * one, and reordering meant clearing it and starting again.
   */
  function handleChange(key, index, teamId) {
    setDrafts((prev) => {
      const next = [...(prev[key] || ["", "", "", ""])];
      const existing = next.findIndex((v) => v && v === teamId);
      if (existing >= 0 && existing !== index) next[existing] = next[index] || "";
      next[index] = teamId;
      return { ...prev, [key]: next };
    });
  }

  function handleClear(key, index) {
    setDrafts((prev) => {
      const next = [...(prev[key] || ["", "", "", ""])];
      next[index] = "";
      return { ...prev, [key]: next };
    });
  }

  function handleClearAll() {
    const cleared = {};
    divisions.forEach((div) => { cleared[keyFor(div)] = ["", "", "", ""]; });
    setDrafts(cleared);
    setError("");
  }

  async function handleSaveAll() {
    if (!savable.length) return;
    setSavingKey("all");
    setError("");
    try {
      // Sequential rather than parallel: the server writes one division per
      // request, and a partial failure is easier to reason about in order.
      for (const div of savable) {
        await savePickemDivision(effectiveSeasonId, poolId, {
          conference: div.conference,
          division: div.division,
          positions: draftFor(div).filter(Boolean).map(Number),
        });
      }
      toast({
        title: savable.length === 1
          ? "1 division saved."
          : `${savable.length} divisions saved.`,
        status: "success", duration: 2500, isClosable: true,
      });
      // Everything pending has been written, so reseed wholesale.
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save. Nothing further was sent.");
    } finally {
      setSavingKey(null);
    }
  }

  const locked = !!data?.locked;
  const divisions = data?.divisions || [];

  // What the server holds, as opposed to what is on screen. The banner used to
  // count drafts and call them "saved", so unsaved work read as stored — and
  // leaving the page lost it.
  const savedFor = (div) => (div.prediction || []).map((v) => (v ? String(v) : ""));
  const draftFor = (div) => drafts[keyFor(div)] || ["", "", "", ""];
  const isComplete = (div) => draftFor(div).filter(Boolean).length === SLOTS.length;
  const isDirty = (div) => {
    const draft = draftFor(div);
    const saved = savedFor(div);
    return SLOTS.some((_, i) => (draft[i] || "") !== (saved[i] || ""));
  };

  const dirty = divisions.filter(isDirty);
  const savable = dirty.filter(isComplete);
  const incompleteDirty = dirty.length - savable.length;
  const anyPlaced = divisions.some((d) => draftFor(d).some(Boolean));

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="NFL PICK 'EM DIVISION PREDICTIONS"
        user={user}
        onLogout={handleLogout}
        onBack={() => navigate(`/nfl-pickem/current/${poolId}`,
                               { state: { seasonId: effectiveSeasonId } })}
      />

      <Container maxW="container.xl" py={{ base: 5, md: 8 }}>
        {loading ? (
          <Flex justify="center" py={16}><Spinner size="xl" /></Flex>
        ) : (
          <VStack align="stretch" spacing={5}>
            <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
              <Button size="sm" bg="white" color={TOP_BAR_BLUE} _hover={{ bg: "gray.100" }}
                      onClick={() => navigate(`/nfl-pickem/current/${poolId}`,
                                              { state: { seasonId: effectiveSeasonId } })}>
                Return to Leaderboard
              </Button>

              {!locked && (
                <HStack spacing={3}>
                  {incompleteDirty > 0 && (
                    <Text fontSize="xs" color="orange.200">
                      All four slots are needed to save.
                    </Text>
                  )}
                  <Button size="sm" bg={DEEP_END_RED} color="white"
                          _hover={{ bg: "#a50000" }}
                          isLoading={savingKey === "all"} loadingText="Saving..."
                          isDisabled={!savable.length}
                          onClick={handleSaveAll}>
                    Save All{savable.length ? ` (${savable.length})` : ""}
                  </Button>
                  <Button size="sm" variant="outline" color="white"
                          borderColor="whiteAlpha.700"
                          _hover={{ bg: "whiteAlpha.200" }}
                          isDisabled={savingKey === "all" || !anyPlaced}
                          onClick={handleClearAll}>
                    Clear All
                  </Button>
                </HStack>
              )}
            </Flex>

            {error && (
              <Alert status="error" borderRadius="md" color="red.900">
                <AlertIcon />{error}
              </Alert>
            )}

            {locked ? (
              <Alert status="warning" borderRadius="md" color="orange.900">
                <AlertIcon />
                <Text>{data.lock_reason}</Text>
              </Alert>
            ) : (
              <Alert status="info" borderRadius="md" color="blue.900">
                <AlertIcon />
                <Text>
                  {/* No saved-count here. It reports what the server holds,
                      which is right but reads as a contradiction next to a
                      board you have just cleared. Each card carries its own
                      Unsaved and Complete badges. */}
                  Predictions are open until Week {data?.lock_week} kicks off
                  {data?.lock_at ? ` (${fmtLockDate(data.lock_at)})` : ""}.{" "}
                  All eight must be complete to be scored.
                </Text>
              </Alert>
            )}

            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
              {divisions.map((div) => {
                const key = keyFor(div);
                return (
                  <DivisionCard
                    key={key}
                    division={div}
                    value={drafts[key] || ["", "", "", ""]}
                    locked={locked}
                    dirty={isDirty(div)}
                    onChange={(i, teamId) => handleChange(key, i, teamId)}
                    onClear={(i) => handleClear(key, i)}
                  />
                );
              })}
            </SimpleGrid>

          </VStack>
        )}
      </Container>
    </Box>
  );
}
